import { Check, Info, Pencil } from "lucide-react";

import type { AfstandOpgave } from "../../afstand/afstandApi";

/**
 * Het dossier zoals de eigenaar het terugziet nadat hij heeft ingediend.
 *
 * <p><b>Dit is wat hij bij terugkomst hoort te zien.</b> Niet het formulier opnieuw vanaf stap 1,
 * maar een overzicht van wat hij heeft doorgegeven, met per onderdeel een potlood om het te wijzigen
 * (Maarten, 26-10-2026). Wie zijn dier wegdoet tikt daar een halfuur aan gegevens in; dat terug
 * moeten lopen om één telefoonnummer te verbeteren is onredelijk.
 *
 * <p><b>Wijzigen mag zolang de aanvraag loopt.</b> Het asiel heeft hem in behandeling, niet in beton.
 * Een afgesloten aanvraag toont geen potloden meer -- daar valt niets meer aan te veranderen.
 *
 * <p>De inhoud komt bij het asiel vandaan en niet uit het formulier in dit tabblad: dat is weg zodra
 * hij het sluit, en het asiel kan intussen iets gewijzigd hebben.
 */
export function AfstandDossier({ opgave, status, uitleg, afgesloten, onBewerk, fotosVan,
  overigeBestanden }: {
  opgave: AfstandOpgave | null;
  /** De foto's van het hoeveelste dier; op volgnummer, zoals het asiel ze teruggeeft. */
  fotosVan?: (volgnummer: number) => { documentId: string; naam: string; src: string | null }[];
  /** Wat er verder is verstuurd: paspoorten, verslagen, het entingenboekje. */
  overigeBestanden?: { documentId: string; naam: string }[];
  status: { kop: string; tekst: string };
  /** Een extra regel boven het dossier, bijvoorbeeld dat het asiel om aanvulling vraagt. */
  uitleg?: React.ReactNode;
  afgesloten: boolean;
  onBewerk: (onderdeel: "gegevens" | "dieren" | "documenten") => void;
}) {
  return (
    <section className="afst__paneel afst__dossier">
      <span className="afst__afgerond-teken" aria-hidden="true"><Check size={26} /></span>
      <h3>Uw afstandsaanvraag</h3>
      <p className="afst__paneeltekst">
        Ingediend bij {opgave?.vestigingNaam ?? "het asiel"}. Hieronder staat wat u heeft doorgegeven.
      </p>

      <p className="afst__stand">{status.kop}</p>
      <p className="afst__paneeltekst" style={{ marginTop: 10 }}>{status.tekst}</p>
      {uitleg}

      {!opgave && <p className="afst__paneeltekst" style={{ marginTop: 20 }}>Bezig met ophalen…</p>}

      {opgave && (
        <div className="afst__dossier-blokken">
          <Blok titel="Uw gegevens" afgesloten={afgesloten} onBewerk={() => onBewerk("gegevens")}>
            <dl className="afst__opgave-lijst">
              <Regel label="Naam" waarde={opgave.contact?.naam} />
              <Regel label="E-mailadres" waarde={opgave.contact?.email} />
              <Regel label="Telefoonnummer" waarde={opgave.contact?.telefoon} />
              <Regel
                label="Adres"
                waarde={[
                  [opgave.contact?.straat, opgave.contact?.huisnummer].filter(Boolean).join(" "),
                  [opgave.contact?.postcode, opgave.contact?.woonplaats].filter(Boolean).join(" "),
                ].filter(Boolean).join(", ")}
              />
            </dl>
          </Blok>

          {opgave.dieren.map((dier, plek) => {
            const antwoorden = opgave.antwoorden.find((a) => a.dierId === dier.id);
            const boekje = opgave.entingen.find((e) => e.dierId === dier.id);
            const gegeven = (antwoorden?.antwoorden ?? []).filter((a) => a.waarde);
            return (
              <Blok key={dier.id} titel={dier.naam || "Dier"} afgesloten={afgesloten}
                    onBewerk={() => onBewerk("dieren")}>
                {/* De foto's bovenaan: hij zoekt zijn eigen dier, en dat herkent hij aan de
                    foto en niet aan de rij gegevens eronder. */}
                {(fotosVan?.(plek) ?? []).length > 0 && (
                  <ul className="afst__dossierfotos">
                    {fotosVan!(plek).map((f) => (
                      <li key={f.documentId}>
                        <img src={f.src ?? undefined} alt={f.naam} />
                      </li>
                    ))}
                  </ul>
                )}

                <dl className="afst__opgave-lijst">
                  <Regel label="Diersoort" waarde={dier.diersoort} />
                  <Regel label="Ras" waarde={dier.ras} />
                  <Regel label="Geslacht" waarde={dier.geslacht} />
                  <Regel label="Geboortedatum" waarde={dier.geboortedatum} />
                  <Regel label="Chipnummer" waarde={dier.chipPasnummer} />
                </dl>

                {/* Alleen beantwoorde vragen: een lijst met twintig streepjes leest als een verwijt,
                    terwijl niet elke vraag voor elk dier van toepassing was. */}
                {gegeven.length > 0 && (
                  <>
                    <h5>Uw antwoorden</h5>
                    <dl className="afst__opgave-lijst">
                      {gegeven.map((a) => (
                        <Regel key={a.veldSleutel} label={a.label} waarde={a.waarde} />
                      ))}
                    </dl>
                  </>
                )}

                {(boekje?.regels ?? []).length > 0 && (
                  <>
                    <h5>Entingen uit het boekje</h5>
                    <ul className="afst__opgave-entingen">
                      {boekje!.regels.map((r, i) => (
                        <li key={i}>
                          {[r.vaccin, r.datum, r.geldigTot ? `geldig tot ${r.geldigTot}` : null]
                            .filter(Boolean).join(" · ")}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Blok>
            );
          })}

          <Blok titel="Documenten" afgesloten={afgesloten} onBewerk={() => onBewerk("documenten")}>
            <p className="afst__paneeltekst" style={{ margin: 0, textAlign: "left" }}>
              De bestanden die u heeft toegevoegd staan bij het asiel. U kunt er hier nog bij doen.
            </p>
            {/* Met naam, zodat hij kan zien of zijn paspoort er echt bij zit. Bij een pdf of verslag
                is de naam het enige dat iets zegt -- een voorbeeld tonen kan hier niet. */}
            {(overigeBestanden ?? []).length > 0 && (
              <ul className="afst__opgave-entingen">
                {overigeBestanden!.map((b) => (
                  <li key={b.documentId}>{b.naam}</li>
                ))}
              </ul>
            )}
          </Blok>
        </div>
      )}

      <p className="afst__tip">
        <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
        Indienen is geen definitieve afstand en geen toezegging van opname. U kunt uw aanvraag volgen
        en aanpassen zolang deze in behandeling is; na verwerking of annulering wordt de
        portaaltoegang beëindigd.
      </p>
    </section>
  );
}

/** Eén onderdeel van het dossier, met het potlood dat het opent. */
function Blok({ titel, afgesloten, onBewerk, children }: {
  titel: string;
  afgesloten: boolean;
  onBewerk: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="afst__dossier-blok">
      <div className="afst__dossier-blokkop">
        <h4>{titel}</h4>
        {/* Geen potlood op een afgesloten aanvraag: een knop die niets mag is erger dan geen knop. */}
        {!afgesloten && (
          <button type="button" className="afst__potlood" onClick={onBewerk}
                  aria-label={`${titel} wijzigen`}>
            <Pencil size={15} aria-hidden="true" />
            Wijzigen
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/** Eén regel; een leeg veld laten we weg in plaats van er een streepje neer te zetten. */
function Regel({ label, waarde }: { label: string; waarde?: string | null }) {
  if (!waarde || waarde.trim() === "") {
    return null;
  }
  return (
    <>
      <dt>{label}</dt>
      <dd>{waarde}</dd>
    </>
  );
}
